import mechanicsJson from "../data/mechanics.json" with { type: "json" };
import themesJson from "../data/themes.json" with { type: "json" };
import audiencesJson from "../data/audiences.json" with { type: "json" };
import formatsJson from "../data/formats.json" with { type: "json" };
import componentsJson from "../data/components.json" with { type: "json" };
import erasJson from "../data/eras.json" with { type: "json" };
import reviewersJson from "../data/reviewers.json" with { type: "json" };
import channelsJson from "../data/channels.json" with { type: "json" };
import factoriesJson from "../data/factories.json" with { type: "json" };
import playtestJson from "../data/playtest.json" with { type: "json" };
import staffJson from "../data/staff.json" with { type: "json" };
import studioJson from "../data/studio.json" with { type: "json" };
import rivalsJson from "../data/rivals.json" with { type: "json" };
import combosJson from "../data/combos.json" with { type: "json" };
import furnitureJson from "../data/furniture.json" with { type: "json" };
import petsJson from "../data/pets.json" with { type: "json" };
import marketingJson from "../data/marketing.json" with { type: "json" };
import eventsJson from "../data/events.json" with { type: "json" };
import arcsJson from "../data/arcs.json" with { type: "json" };
import achievementsJson from "../data/achievements.json" with { type: "json" };
import legendsJson from "../data/legends.json" with { type: "json" };
import conventionsJson from "../data/conventions.json" with { type: "json" };
import musicJson from "../data/music.json" with { type: "json" };
import sizesJson from "../data/sizes.json" with { type: "json" };
import storesJson from "../data/stores.json" with { type: "json" };
import goalsJson from "../data/goals.json" with { type: "json" };
import directionsJson from "../data/directions.json" with { type: "json" };
import finaleJson from "../data/finale.json" with { type: "json" };
import namesJson from "../data/names.json" with { type: "json" };
import studiesJson from "../data/studies.json" with { type: "json" };
import staffPerksJson from "../data/staffperks.json" with { type: "json" };
import checkinsJson from "../data/checkins.json" with { type: "json" };
import tipsJson from "../data/tips.json" with { type: "json" };
import barksJson from "../data/barks.json" with { type: "json" };
import gardenJson from "../data/garden.json" with { type: "json" };
import perksJson from "../data/perks.json" with { type: "json" };
import licencesJson from "../data/licences.json" with { type: "json" };
import cardsJson from "../data/cards.json" with { type: "json" };
import ngplusJson from "../data/ngplus.json" with { type: "json" };
import decadesJson from "../data/decades.json" with { type: "json" };
import fanmailJson from "../data/fanmail.json" with { type: "json" };
import type {
  Achievement, Arc, Audience, AwardDef, CardRules, LicenceDef, NgPlus, Channel, Combo, ComponentOption, Convention, EventDef, FurnitureKind, GardenPlant, Legend, MarketingKind, MusicTrack, Perk, PetBreed, Era, Factory, Format, Mechanic, Personality, Problem, Reviewer, Rival, Role,
  RoomType, Slider, StudioLevel, Theme, TesterPool, Trait, Annex, GameSize, StoreDef, GoalDef, DirectionDef, FinaleTitle, TipDef, NameLists, StudyDef, StaffPerkTier, CheckinDef, DecadeDef, ShowDef, FanMailContent } from "./types";

export * from "./types";

function byId<T extends { id: string }>(list: T[]): Record<string, T> {
  const out: Record<string, T> = {};
  for (const item of list) out[item.id] = item;
  return out;
}

export const mechanics = mechanicsJson as unknown as Mechanic[];
export const themes = themesJson as unknown as Theme[];
export const audiences = audiencesJson as unknown as Audience[];
export const formats = formatsJson as unknown as Format[];
export const components = componentsJson as unknown as ComponentOption[];
export const eras = erasJson as Era[];
export const reviewers = reviewersJson as unknown as Reviewer[];
export const channels = channelsJson as unknown as Channel[];
export const factories = factoriesJson as unknown as Factory[];
export const sliders = playtestJson.sliders as Slider[];
export const problems = playtestJson.problems as unknown as Problem[];
export const personalities = playtestJson.personalities as unknown as Personality[];
export const testerPools = playtestJson.pools as unknown as TesterPool[];
export const roles = staffJson.roles as Role[];
export const traits = staffJson.traits as unknown as Trait[];
export const staffNames = staffJson.names;
export const studioLevels = studioJson.levels as StudioLevel[];
export const annexes = studioJson.annexes as unknown as Annex[];
export const roomTypes = studioJson.rooms as unknown as RoomType[];
export const rivals = rivalsJson as unknown as Rival[];
export const combos = combosJson as unknown as Combo[];
export const furniture = furnitureJson as unknown as FurnitureKind[];
export const petBreeds = petsJson.breeds as PetBreed[];
export const petNames = petsJson.names;
export const petSpots = petsJson.spots;
export const petQuirks = petsJson.quirks;
export const marketingKinds = marketingJson as unknown as MarketingKind[];
export const events = eventsJson as unknown as EventDef[];
export const arcs = arcsJson as unknown as Arc[];
export const achievements = achievementsJson as unknown as Achievement[];
export const legends = legendsJson as unknown as Legend[];
export const conventions = conventionsJson.cons as unknown as Convention[];
export const awards = conventionsJson.awards as unknown as AwardDef[];
/** Fairs with a booth to book but no awards (the award conventions take bookings too). */
export const shows = conventionsJson.shows as unknown as ShowDef[];
/** What the log says after a booth: by how the weekend went. */
export const boothLines = conventionsJson.boothLines as Record<"busy" | "steady" | "quiet" | "projectBusy" | "projectQuiet" | "nominated" | "unused", string>;
/** One bigger strategic choice at the start of each decade, 1970 to 2030. */
export const decades = decadesJson as unknown as DecadeDef[];
/** Letters from fans about your games, and the follow-ups some of them ask for. */
export const fanMail = fanmailJson as unknown as FanMailContent;
export const music = musicJson as unknown as MusicTrack[];
export const gameSizes = sizesJson as unknown as GameSize[];
export const stores = storesJson as unknown as StoreDef[];
export const goals = goalsJson as unknown as GoalDef[];
export const directions = directionsJson as unknown as DirectionDef[];
export const finaleTitles = finaleJson as unknown as FinaleTitle[];
/** Word lists for game names: per-theme words, forms, and follow-up subtitles. */
export const names = namesJson as unknown as NameLists;
/** Repeatable research: market studies, surveys, house rules engines. */
export const studies = studiesJson as unknown as StudyDef[];
/** Strengths people choose at levels 3, 5, 8, 12 and 16. */
export const staffPerks = staffPerksJson as unknown as StaffPerkTier[];
/** Halfway check-ins on a project: small choices while the work goes on. */
export const checkins = checkinsJson as unknown as CheckinDef[];
export const tips = tipsJson as unknown as TipDef[];
/** What people in the studio say: lines for events, idle chatter by trait, and general mutters. */
export const barks = barksJson as { events: Record<string, string[]>; idle: Record<string, string[]>; generic: string[] };
export const gardenPlants = gardenJson as unknown as GardenPlant[];
export const perks = perksJson as unknown as Perk[];
export const licences = licencesJson as unknown as LicenceDef[];
export const cardRules = cardsJson as unknown as CardRules;
/** New Game+: the secret alternate timeline, unlocked by finishing a season. */
export const ngPlus = ngplusJson as unknown as NgPlus;

export const C = {
  mechanic: byId(mechanics),
  theme: byId(themes),
  audience: byId(audiences),
  format: byId(formats),
  component: byId(components),
  era: byId(eras),
  reviewer: byId(reviewers),
  channel: byId(channels),
  factory: byId(factories),
  problem: byId(problems),
  personality: byId(personalities),
  pool: byId(testerPools),
  role: byId(roles),
  trait: byId(traits),
  level: byId(studioLevels),
  annex: byId(annexes),
  size: byId(gameSizes),
  store: byId(stores),
  goal: byId(goals),
  direction: byId(directions),
  room: byId(roomTypes),
  rival: byId(rivals),
  furniture: byId(furniture),
  breed: byId(petBreeds),
  marketing: byId(marketingKinds),
  event: byId(events),
  arc: byId(arcs),
  achievement: byId(achievements),
  legend: byId(legends),
  convention: byId(conventions),
  show: byId(shows),
  decade: byId(decades),
  award: byId(awards),
  perk: byId(perks),
  plant: byId(gardenPlants),
  licence: byId(licences),
  archetype: byId(cardRules.archetypes),
  keyword: byId(cardRules.keywords),
};

export function eraForYear(year: number): Era {
  for (const e of eras) if (year >= e.from && year <= e.to) return e;
  return year < eras[0].from ? eras[0] : eras[eras.length - 1];
}
